// Storage utility
// Projects/folders stored on backend (primary) + IndexedDB (cache)
// PDF files stored on backend server

const DB_NAME = 'PDFViewerDB';
const DB_VERSION = 2;
const PROJECTS_STORE = 'projects';

import { isRegionMarkup } from '../components/shared/regionMarkup';
import { BACKEND_URL, DETECTOR_URL } from './config.js';
import { buildBase, fingerprint } from './settingsFingerprint';
import { apiFetch } from './apiFetch.js';
import { isQuickViewId, inQuickView } from './quickViewGuard.js';
import { ensureFileCached } from './sharepoint';
import { getHandle, verifyPermission } from './fileHandleStore.js';

// Custom error type the PDF loader throws when a locally-linked file's
// permission has lapsed (e.g. after a page reload) and the user needs to
// re-grant access. Callers can catch this and show an inline "Grant access"
// button that re-invokes the loader inside a user gesture.
export class LocalFilePermissionError extends Error {
  constructor(file) {
    super(`Permission required to read "${file?.name || 'file'}"`);
    this.name = 'LocalFilePermissionError';
    this.file = file;
  }
}

let db = null;

// =============================================================================
// CACHING LAYER - Reduces redundant network requests
// =============================================================================

const cache = {
  objects: new Map(),      // projectId -> { data, timestamp }
  regions: new Map(),      // projectId -> { data, timestamp }
  models: new Map(),       // projectId -> { data, timestamp }
  pdfUrls: new Map(),      // filename -> blobUrl
  pendingRequests: new Map(), // Deduplication of in-flight requests
};

const CACHE_TTL = 30000; // 30 seconds

const getCached = (cacheMap, key) => {
  const cached = cacheMap.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  // EXPIRED ENTRIES ARE DELETED, not just ignored. Returning null while
  // leaving the entry in the Map meant a stale payload — for a P&ID project
  // that is every detected object with its bbox and properties — stayed
  // referenced for the life of the tab, for every project ever opened.
  if (cached) cacheMap.delete(key);
  return null;
};

const setCache = (cacheMap, key, data) => {
  cacheMap.set(key, { data, timestamp: Date.now() });
};

const invalidateCache = (cacheMap, key) => {
  cacheMap.delete(key);
};

// Deduplicate concurrent requests to same endpoint
const dedupeRequest = async (key, requestFn) => {
  if (cache.pendingRequests.has(key)) {
    return cache.pendingRequests.get(key);
  }
  
  const promise = requestFn().finally(() => {
    cache.pendingRequests.delete(key);
  });
  
  cache.pendingRequests.set(key, promise);
  return promise;
};

// =============================================================================
// IndexedDB Functions
// =============================================================================

// In-memory fallback when IndexedDB is completely broken
let useMemoryFallback = false;
const memoryStore = new Map();

// Initialize IndexedDB (with auto-recovery on corruption)
let initAttempted = false;

export const initDB = () => {
  return new Promise((resolve, reject) => {
    // If already using memory fallback, return immediately
    if (useMemoryFallback) {
      resolve(null);
      return;
    }

    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Failed to open IndexedDB:', request.error);

      // If first attempt fails, try deleting the corrupted database and recreating
      if (!initAttempted) {
        initAttempted = true;
        console.warn('IndexedDB corrupted — attempting to delete and recreate...');

        const deleteRequest = indexedDB.deleteDatabase(DB_NAME);
        deleteRequest.onsuccess = () => {
          console.log('Corrupted database deleted, recreating...');
          db = null;
          initDB().then(resolve).catch(reject);
        };
        deleteRequest.onerror = () => {
          console.warn('Cannot delete corrupted IndexedDB — falling back to in-memory storage.');
          console.warn('Clear site data in browser settings (F12 → Application → Storage → Clear site data) to fix permanently.');
          useMemoryFallback = true;
          resolve(null);
        };
      } else {
        console.warn('IndexedDB still failing — falling back to in-memory storage.');
        console.warn('Clear site data in browser settings (F12 → Application → Storage → Clear site data) to fix permanently.');
        useMemoryFallback = true;
        resolve(null);
      }
    };

    request.onsuccess = () => {
      db = request.result;
      initAttempted = false;

      // Clear stale reference if the connection closes unexpectedly
      db.onclose = () => { db = null; };
      db.onversionchange = () => { db.close(); db = null; };

      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      if (!database.objectStoreNames.contains(PROJECTS_STORE)) {
        database.createObjectStore(PROJECTS_STORE, { keyPath: 'id' });
      }
    };
  });
};

// Retry helper — if a transaction fails because the connection closed,
// null out `db` so initDB() re-opens it, then retry once.
const idbTransaction = async (mode, fn) => {
  await initDB();
  if (useMemoryFallback) return null; // caller handles fallback before calling this

  const attempt = () => new Promise((resolve, reject) => {
    try {
      const transaction = db.transaction([PROJECTS_STORE], mode);
      fn(transaction, resolve, reject);
    } catch (err) {
      reject(err);
    }
  });

  try {
    return await attempt();
  } catch (err) {
    if (err.name === 'InvalidStateError') {
      // Connection closed — re-open and retry once
      db = null;
      await initDB();
      if (useMemoryFallback) return null;
      return await attempt();
    }
    throw err;
  }
};

// Strip per-user runtime state from a project tree before persisting.
// `checkedOutByMe` is derived per-user from `checkedOutBy` — it must NEVER
// be saved or it leaks across accounts (account A's "true" becomes account
// B's "true" when they fetch the project). Defensive — never throws on
// weird shape, just returns the project unchanged on error.
// What each project held when this browser last LOADED or SAVED it
// (fingerprints — utils/settingsFingerprint.js). Sent with the next save as
// `_base`, so the server writes only what that save changed and keeps edits
// made elsewhere in the meantime. No entry (never loaded here) = a plain save.
const baseByProject = new Map();

function stripPerUserState(project) {
  if (!project || typeof project !== 'object') return project;
  const sanitizeFile = (f) => {
    if (!f || typeof f !== 'object') return f;
    let out = f;
    if (f.sharepoint) {
      const { checkedOutByMe, ...rest } = f.sharepoint;
      out = { ...out, sharepoint: rest };
    }
    // Board rows mirror the same rule on their source-lock overlay.
    if (f.boardSource && f.boardSource.checkedOutByMe !== undefined) {
      const { checkedOutByMe: _bMine, ...bRest } = f.boardSource;
      out = { ...out, boardSource: bRest };
    }
    return out;
  };
  const sanitizeFolder = (folder) => {
    if (!folder || typeof folder !== 'object') return folder;
    return {
      ...folder,
      files: Array.isArray(folder.files) ? folder.files.map(sanitizeFile) : folder.files,
      subfolders: Array.isArray(folder.subfolders) ? folder.subfolders.map(sanitizeFolder) : folder.subfolders,
    };
  };
  try {
    return {
      ...project,
      files:   Array.isArray(project.files)   ? project.files.map(sanitizeFile)     : project.files,
      folders: Array.isArray(project.folders) ? project.folders.map(sanitizeFolder) : project.folders,
    };
  } catch (e) {
    console.error('[storage] stripPerUserState failed:', e);
    return project;
  }
}

// ── A Quick View's record lives in the TAB ──────────────────────────────
// A Quick View (`project_quick_*`) keeps nothing on the server
// (utils/quickViewGuard): its record — the file list and the handles' keys —
// is kept in sessionStorage, so a reload in the same tab finds it and closing
// the tab discards it. Never IndexedDB, never the account.
const QUICK_RECORD = 'pidly.quickView.';
const quickRecordRead = (id) => {
  try { const raw = sessionStorage.getItem(QUICK_RECORD + id); return raw ? JSON.parse(raw) : null; } catch { return null; }
};
const quickRecordWrite = (project) => {
  try { sessionStorage.setItem(QUICK_RECORD + project.id, JSON.stringify(project)); } catch { /* full or private: the tab still holds it in memory */ }
};
const quickRecordDrop = (id) => {
  try { sessionStorage.removeItem(QUICK_RECORD + id); } catch { /* private mode */ }
};

// Save a project — backend is primary, IndexedDB is cache
export const saveProject = async (project) => {
  if (isQuickViewId(project?.id)) { quickRecordWrite(stripPerUserState(project)); return; }
  const sanitized = stripPerUserState(project);
  // Always save to backend (primary — survives browser data wipes).
  // A NETWORK failure falls back to IndexedDB-only (offline). A server
  // REJECTION (403 permission guard, 500) must THROW instead: it used
  // to be swallowed too, so the UI showed the edit as saved, the local
  // cache kept it, and the change silently vanished on the next reload
  // — "I added a description and it's not showing".
  try {
    const r = await apiFetch(`/api/projects/${encodeURIComponent(sanitized.id)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ project: baseByProject.has(sanitized.id) ? { ...sanitized, _base: baseByProject.get(sanitized.id) } : sanitized }),
    });
    if (!r.ok) {
      let msg = `Save failed (${r.status})`;
      try { const d = await r.json(); if (d?.error) msg = d.error; } catch { /* keep status message */ }
      throw new Error(msg);
    }
    // What the server now holds from this browser's point of view.
    try { baseByProject.set(sanitized.id, buildBase(sanitized)); } catch { /* no base: a plain save next time */ }
  } catch (err) {
    // fetch() throws TypeError on network failure — that's the offline
    // case the IndexedDB fallback exists for. Anything else is a real
    // rejection the caller must see.
    if (!(err instanceof TypeError)) throw err;
    console.warn('[saveProject] Backend unreachable, using IndexedDB only:', err.message);
  }

  // Also cache in IndexedDB for fast offline reads
  await initDB();
  if (useMemoryFallback) {
    memoryStore.set(sanitized.id, JSON.parse(JSON.stringify(sanitized)));
    return;
  }
  try {
    await idbTransaction('readwrite', (tx, resolve, reject) => {
      const store = tx.objectStore(PROJECTS_STORE);
      const request = store.put(sanitized);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (e) { /* IndexedDB cache failure is non-fatal */ }
};

// Workspace details (name, description, client, tags, icon) — PATCH
// /api/projects/:id/details. Never saveProject() for these: a project object
// from the workspace LIST has no property values or templates, and a whole
// project PUT of it wiped them.
export const saveProjectDetails = async (projectId, details) => {
  const r = await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/details`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(details || {}),
  });
  let d = null;
  try { d = await r.json(); } catch { /* keep status message */ }
  if (!r.ok || !d?.success) throw new Error(d?.error || `Save failed (${r.status})`);
  return d;
};

// Granular doc-property save — merges ONLY the given per-file property patches
// server-side (PATCH /api/projects/:id/doc-properties), instead of PUTing the
// whole project. This is what makes doc-prop saving safe: the whole-project PUT
// is last-write-wins, so a change made on the other surface (or by another user)
// since the local copy loaded gets clobbered; this touches just the patched
// fields and is optimistic-concurrency guarded server-side.
//
// `patches` is an array of:
//   { fileId, page: null|N,
//     props?: { name: value|null },       // shallow-merge extractedProperties (null deletes a key)
//     propsReplace?: {...}|'clear',        // full replace / clear extractedProperties
//     docPropTemplateId?, docPropIssues?, docPropTemplateMismatch?,
//     docPropAuditedAt?, docPropExtractionFailed?  // absent = leave, null = delete, else set
//   }
// Throws on failure so callers can surface a save error rather than silently
// dropping the edit (the whole-project PUT swallowed backend errors).
// Count of doc-property PATCH saves currently in flight. Any live-sync refresh
// (MainView / the /docprops page) checks this and holds off while it's > 0 so a
// server re-fetch can't briefly revert an edit that hasn't landed yet.
let _docPropSavesInFlight = 0;
export const docPropSavesInFlight = () => _docPropSavesInFlight;

export const saveDocProps = async (projectId, patches) => {
  _docPropSavesInFlight += 1;
  try {
    const res = await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/doc-properties`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ patches }),
    });
    if (!res.ok) {
      let msg = `Doc-property save failed (${res.status})`;
      try { const j = await res.json(); if (j?.error) msg = j.error; } catch { /* keep default */ }
      throw new Error(msg);
    }
    return res.json();
  } finally {
    _docPropSavesInFlight -= 1;
  }
};

// Get all projects — try backend first, fall back to IndexedDB.
// `scope` may be 'personal' (default) or a workspace/org id.
export const getAllProjects = async (scope = 'personal') => {
  // Try backend first (authoritative)
  try {
    const url = `/api/projects?scope=${encodeURIComponent(scope)}`;
    const response = await apiFetch(url);
    if (response.ok) {
      const data = await response.json();
      const projects = data.projects || [];
      // Backfill IndexedDB cache — MERGED OVER the existing record, never
      // replacing it.
      //
      // The list response is deliberately slimmer than the full project (the
      // backend drops per-file document properties and the template library;
      // see projectToListJson) because shipping every project's whole blob to
      // draw a list was freezing the page. A blind `put` would therefore
      // OVERWRITE a fully-cached project with the slim one, and the offline
      // read path plus the whole-project PUT would then write that loss back
      // — the clobber trap in CLAUDE.md. Spreading the slim row over the
      // cached one keeps any key the list omitted.
      if (projects.length > 0) {
        await initDB();
        for (const p of projects) {
          try {
            if (useMemoryFallback) {
              memoryStore.set(p.id, { ...(memoryStore.get(p.id) || {}), ...p });
            } else {
              await idbTransaction('readwrite', (tx, resolve, reject) => {
                const store = tx.objectStore(PROJECTS_STORE);
                const read = store.get(p.id);
                read.onsuccess = () => {
                  const merged = { ...(read.result || {}), ...p };
                  const w = store.put(merged);
                  w.onsuccess = () => resolve();
                  w.onerror = () => reject(w.error);
                };
                read.onerror = () => reject(read.error);
              });
            }
          } catch (e) { /* non-fatal */ }
        }
      }
      return projects;
    }
  } catch (err) {
    console.warn('[getAllProjects] Backend unreachable, falling back to IndexedDB:', err.message);
  }

  // Fallback to IndexedDB
  await initDB();
  if (useMemoryFallback) {
    return Array.from(memoryStore.values());
  }
  return idbTransaction('readonly', (tx, resolve, reject) => {
    const store = tx.objectStore(PROJECTS_STORE);
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

// Get a single project. Backend is authoritative for access control;
// we only fall back to IndexedDB when the backend is genuinely
// unreachable (network error), never when it returns 401/403/404.
// Otherwise a logged-in user could "open" another user's project
// just because it was cached in this browser from a prior session.
// Workspaces the server refused to open for this person (no longer a member).
const projectDenials = new Set();
/** True when the last read of this workspace was refused for access. */
export const wasProjectDenied = (projectId) => projectDenials.has(projectId);

/**
 * The save base describes what the CLIENT HOLDS, never simply the latest
 * read. A refresh that merges only some keys into local state (doc props,
 * templates, classes) used to reset it to the server's whole copy, so the
 * keys left stale locally (links, the file tree) looked changed by this
 * client: its next save put them back and the activity feed credited it with
 * removing and re-pointing links a colleague had made. Callers that replace
 * their whole copy pass `adoptBase`; a partial merge calls adoptProjectBase
 * with the merged result.
 */
export const adoptProjectBase = (project) => {
  if (!project?.id) return;
  try { baseByProject.set(project.id, buildBase(stripPerUserState(project))); } catch { /* no base: a plain save */ }
};

/**
 * True when this client has NOT changed a document's links since it last
 * loaded or saved them (its list matches the save base). A live update may
 * then replace them with the server's, so a colleague's or a detection run's
 * new links appear here instead of going stale and being deleted by this
 * client's next save.
 */
export const hotspotsUnchangedHere = (projectId, fid, list) => {
  const base = baseByProject.get(projectId);
  if (!base || !base.hotspots) return false;
  if (!Object.prototype.hasOwnProperty.call(base.hotspots, fid)) return list === undefined || (Array.isArray(list) && list.length === 0);
  return fingerprint(list) === base.hotspots[fid];
};

export const getProject = async (projectId, { adoptBase = false } = {}) => {
  if (isQuickViewId(projectId)) return quickRecordRead(projectId);
  projectDenials.delete(projectId);
  try {
    const response = await apiFetch(`/api/projects/${encodeURIComponent(projectId)}`);
    if (response.ok) {
      const data = await response.json();
      const project = data.project || null;
      if (project?.id && (adoptBase || !baseByProject.has(project.id))) adoptProjectBase(project);
      return project;
    }
    // Non-2xx from backend: authoritative. Don't leak cached data.
    // A refusal is remembered so the workspace page can say why.
    if (response.status === 403 || response.status === 401) projectDenials.add(projectId);
    return null;
  } catch (err) {
    console.warn('[getProject] Backend unreachable, falling back to IndexedDB:', err.message);
  }

  // Genuine network failure — read the local cache so the user can keep
  // working offline. No silent PUT-back: the next successful network
  // save will sync changes; silently uploading cached state under the
  // current user's identity is how one user ends up overwriting another.
  await initDB();
  if (useMemoryFallback) return memoryStore.get(projectId) || null;
  return idbTransaction('readonly', (tx, resolve, reject) => {
    const store = tx.objectStore(PROJECTS_STORE);
    const request = store.get(projectId);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
};

// Delete a project — delete from both backend and IndexedDB
// `keepalive` lets the request outlive the page transition. Deletes fired
// from an unmount cleanup (the quick-view sweep) were being cancelled by the
// navigation that triggered them, and the swallowed failure below meant the
// row stayed on the server with nothing said about it.
export const deleteProject = async (projectId, { keepalive = false } = {}) => {
  if (isQuickViewId(projectId)) { quickRecordDrop(projectId); return; }
  // Delete from backend
  try {
    await apiFetch(`/api/projects/${encodeURIComponent(projectId)}`, { method: 'DELETE', keepalive });
  } catch (e) { /* non-fatal */ }

  // Delete from IndexedDB cache
  await initDB();
  if (useMemoryFallback) {
    memoryStore.delete(projectId);
    return;
  }
  try {
    await idbTransaction('readwrite', (tx, resolve, reject) => {
      const store = tx.objectStore(PROJECTS_STORE);
      const request = store.delete(projectId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (e) { /* non-fatal */ }
};

// Wipe the local project cache (IndexedDB + in-memory). Call this on
// sign-out so the next user to log in on this browser doesn't see the
// previous user's projects through the cache. Backend access control
// is still authoritative — this just prevents the stale-display leak.
// Named distinctly from `clearProjectCache(projectId)` further down
// which only invalidates a single project's in-memory entry.
export const wipeLocalProjectData = async () => {
  try { memoryStore.clear(); } catch {}
  // Also drop the in-process request + data caches.
  try {
    cache.objects.clear();
    cache.regions.clear();
    cache.models.clear();
    cache.pdfUrls.clear();
    cache.pendingRequests.clear();
  } catch {}
  try {
    await initDB();
    if (useMemoryFallback) return;
    await idbTransaction('readwrite', (tx, resolve, reject) => {
      const r = tx.objectStore(PROJECTS_STORE).clear();
      r.onsuccess = () => resolve();
      r.onerror = () => reject(r.error);
    });
  } catch (e) {
    console.warn('[storage] clearProjectCache IDB clear failed:', e.message);
  }
};

// Migrate from localStorage (if any old data exists)
export const migrateFromLocalStorage = async () => {
  const savedProjects = localStorage.getItem('projects');
  if (!savedProjects) return;

  try {
    const projects = JSON.parse(savedProjects);
    
    for (const project of projects) {
      // Save project to IndexedDB (without file data - that's on backend now)
      const cleanProject = {
        ...project,
        folders: project.folders?.map(folder => ({
          ...folder,
          files: folder.files?.map(file => ({
            id: file.id,
            name: file.name,
            backendFilename: file.backendFilename || file.name, // Use name as fallback
            folderId: file.folderId,
            uploadedAt: file.uploadedAt
          })) || []
        })) || []
      };
      await saveProject(cleanProject);
    }

    // Clear localStorage after migration
    localStorage.removeItem('projects');
    console.log('Migration from localStorage complete');
  } catch (error) {
    console.error('Migration failed:', error);
  }
};

// =============================================================================
// Project Line Styles (stored on the project object in IndexedDB)
// =============================================================================

// ── Persistence ──────────────────────────────────────────────────────────
// Through a DEDICATED endpoint, not saveProject(). These are authored in one
// place and read everywhere an annotation is drawn, and saveProject is a
// whole-project PUT — a stale one from another tab would revert them. Same
// treatment title block templates and detection presets already have.
const putLineStyles = async (projectId, lineStyles) => {
  const response = await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/line-styles`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lineStyles: lineStyles || [] }),
  });
  if (!response.ok) {
    let msg = `Server returned ${response.status}`;
    try { const j = await response.json(); msg = j.error || msg; } catch { /* non-JSON */ }
    throw new Error(msg);
  }
  return response.json();
};

// Get all line styles for a project.
//
// Reads from settings, falling back to the old top-level project.lineStyles so
// styles saved before the dedicated endpoint are not lost.
export const getProjectLineStyles = async (projectId) => {
  const project = await getProject(projectId);
  return project?.settings?.lineStyles || project?.lineStyles || [];
};

// Save a new line style (returns 'saved' | 'duplicate')
export const addProjectLineStyle = async (projectId, style) => {
  const styles = await getProjectLineStyles(projectId);
  if (styles.some(s => s.name === style.name)) return 'duplicate';
  await putLineStyles(projectId, [...styles, { ...style, savedAt: Date.now() }]);
  return 'saved';
};

// Replace a style by name, or add it when there is nothing to replace.
export const updateProjectLineStyle = async (projectId, name, style) => {
  const styles = await getProjectLineStyles(projectId);
  const i = styles.findIndex(s => s.name === name);
  const next = [...styles];
  if (i >= 0) next[i] = { ...styles[i], ...style };
  else next.push({ ...style, savedAt: Date.now() });
  await putLineStyles(projectId, next);
  return 'saved';
};

// Remove a line style from a project by name
export const removeProjectLineStyle = async (projectId, styleName) => {
  const styles = await getProjectLineStyles(projectId);
  await putLineStyles(projectId, styles.filter(s => s.name !== styleName));
};

// ── Custom HATCHES — the fill counterpart of named line styles ──────────
// project.settings.hatchStyles, through its own endpoint for the same
// reason line styles have one (a stale whole-project PUT must not revert
// them). Each: { name, layers, opacity?, color?, savedAt }.
const putHatchStyles = async (projectId, hatchStyles) => {
  const response = await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/hatch-styles`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ hatchStyles: hatchStyles || [] }),
  });
  if (!response.ok) {
    let msg = `Server returned ${response.status}`;
    try { const j = await response.json(); msg = j.error || msg; } catch { /* non-JSON */ }
    throw new Error(msg);
  }
  return response.json();
};

export const getProjectHatchStyles = async (projectId) => {
  const project = await getProject(projectId);
  return project?.settings?.hatchStyles || project?.hatchStyles || [];
};

export const addProjectHatchStyle = async (projectId, hatch) => {
  const list = await getProjectHatchStyles(projectId);
  if (list.some(h => h.name === hatch.name)) return 'duplicate';
  await putHatchStyles(projectId, [...list, { ...hatch, savedAt: Date.now() }]);
  return 'saved';
};

export const updateProjectHatchStyle = async (projectId, name, hatch) => {
  const list = await getProjectHatchStyles(projectId);
  if (hatch.name !== name && list.some(h => h.name === hatch.name)) return 'duplicate';
  const i = list.findIndex(h => h.name === name);
  const next = [...list];
  if (i >= 0) next[i] = { ...list[i], ...hatch };
  else next.push({ ...hatch, savedAt: Date.now() });
  await putHatchStyles(projectId, next);
  return 'saved';
};

export const removeProjectHatchStyle = async (projectId, name) => {
  const list = await getProjectHatchStyles(projectId);
  await putHatchStyles(projectId, list.filter(h => h.name !== name));
};

// ── Custom ARROW HEADS — the workspace's own head shapes ────────────────
// project.settings.arrowHeadStyles, through its own endpoint like line styles
// and hatches. Each: { name, shapes, inset, savedAt } (shared/arrowHeads
// definition in head units). A markup carries its head's definition itself,
// so removing one here never blanks existing work.
const putArrowHeadStyles = async (projectId, arrowHeadStyles) => {
  const response = await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/arrow-heads`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ arrowHeadStyles: arrowHeadStyles || [] }),
  });
  if (!response.ok) {
    let msg = `Failed to save arrow heads (${response.status})`;
    try { const j = await response.json(); if (j?.error) msg = j.error; } catch { /* no body */ }
    throw new Error(msg);
  }
};

export const getProjectArrowHeadStyles = async (projectId) => {
  const project = await getProject(projectId);
  return project?.settings?.arrowHeadStyles || [];
};

export const addProjectArrowHeadStyle = async (projectId, head) => {
  const list = await getProjectArrowHeadStyles(projectId);
  if (list.some(h => h.name === head.name)) return 'duplicate';
  await putArrowHeadStyles(projectId, [...list, { ...head, savedAt: Date.now() }]);
  return 'saved';
};

export const updateProjectArrowHeadStyle = async (projectId, name, head) => {
  const list = await getProjectArrowHeadStyles(projectId);
  if (head.name !== name && list.some(h => h.name === head.name)) return 'duplicate';
  const i = list.findIndex(h => h.name === name);
  const next = [...list];
  if (i >= 0) next[i] = { ...list[i], ...head };
  else next.push({ ...head, savedAt: Date.now() });
  await putArrowHeadStyles(projectId, next);
  return 'saved';
};

export const removeProjectArrowHeadStyle = async (projectId, name) => {
  const list = await getProjectArrowHeadStyles(projectId);
  await putArrowHeadStyles(projectId, list.filter(h => h.name !== name));
};

// Update/replace all line styles for a project (bulk save)
// Persist the project's shared detection-pipeline presets via the dedicated
// merge endpoint (see dbRoutes). Throws on failure so the caller can surface a
// real error — unlike saveProject(), which swallows backend errors.
export const saveDetectionPresets = async (projectId, presets) => {
  const response = await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/detection-presets`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ presets }),
  });
  if (!response.ok) {
    let msg = `Server returned ${response.status}`;
    try { const j = await response.json(); msg = j.error || msg; } catch {}
    throw new Error(msg);
  }
  return response.json();
};

// ─── API Data: sources / bindings / lookups (see app/api-data.txt) ─────────
// All per-project endpoints; the backend resolves whether sources live on the
// workspace (org) or the project. Secrets come back MASKED — send the mask
// back unchanged to keep the stored value.
const throwUnlessOk = async (response) => {
  if (response.ok) return response.json();
  let msg = `Server returned ${response.status}`;
  try { const j = await response.json(); msg = j.error || msg; } catch { /* body not json */ }
  const err = new Error(msg);
  err.status = response.status;
  throw err;
};

export const getDataSources = async (projectId) =>
  throwUnlessOk(await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/data-sources`));

// A save tells this tab's readers at once (utils/apiLinksStore): the socket's
// project:changed reaches every OTHER tab but skips this one's own echo.
const announceApiLinks = (projectId) => {
  try { window.dispatchEvent(new CustomEvent('pidly:api-links-changed', { detail: { projectId } })); } catch { /* not a browser */ }
};

export const saveDataSources = async (projectId, sources) => {
  const r = await throwUnlessOk(await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/data-sources`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sources }),
  }));
  announceApiLinks(projectId);
  return r;
};

// `transform`: a link's clean-up rule, applied to the key before the lookup
// (a test made from an object); omitted, the key is looked up as typed.
export const testDataSource = async (projectId, source, sampleKey, transform = null) =>
  throwUnlessOk(await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/data-sources/test`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ source, sampleKey, ...(transform ? { transform } : {}) }),
  }));

export const dataLookup = async (projectId, { sourceId, key, transform, fields }) =>
  throwUnlessOk(await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/data-lookup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sourceId, key, transform, fields }),
  }));

// Close matches for a key that found no row (a register source only).
// `anyColumn`: a search the person refined by hand also looks in the
// register's other columns.
export const dataSearch = async (projectId, { sourceId, query, transform, limit, anyColumn }) =>
  throwUnlessOk(await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/data-search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sourceId, query, transform, limit, anyColumn: !!anyColumn }),
  }));

// A class checked against its register: every tag in one request, asked for
// by the person (the class panel's Pull / Check). `drawingColumn` +
// `drawingKeys` also list the register's rows that belong on this drawing.
export const dataRegister = async (projectId, { sourceId, keys, transform, fields, drawingColumn, drawingKeys, allRows }) =>
  throwUnlessOk(await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/data-register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sourceId, keys, transform, fields, drawingColumn, drawingKeys, allRows: !!allRows }),
  }));

export const resolveSpExcelUrl = async (projectId, url) =>
  throwUnlessOk(await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/sp-excel/resolve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url }),
  }));

export const getApiBindings = async (projectId) =>
  throwUnlessOk(await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/api-bindings`));

export const saveApiBindings = async (projectId, bindings) => {
  const r = await throwUnlessOk(await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/api-bindings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ bindings }),
  }));
  announceApiLinks(projectId);
  return r;
};

// Granular classes save — persists ONLY class definitions (settings.classes /
// classFolders / classColumns) through a dedicated server-side read-modify-write,
// mirroring saveDetectionPresets. Class create/edit/delete/restyle used to ride
// along on the display-only in-memory project and only reached the DB if some
// *other* full-project save happened to flush later — so they silently reverted
// on reload. This writes them immediately and broadcasts 'project' so every
// member's Objects panel + /classes page update live. Only the keys present in
// `payload` are touched. Shares the doc-prop in-flight counter so the live-sync
// re-fetch holds off until the write lands (can't briefly revert a fresh edit).
export const saveClasses = async (projectId, payload) => {
  _docPropSavesInFlight += 1;
  try {
    const response = await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/classes`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload || {}),
    });
    if (!response.ok) {
      let msg = `Server returned ${response.status}`;
      try { const j = await response.json(); msg = j.error || msg; } catch {}
      throw new Error(msg);
    }
    return response.json();
  } finally {
    _docPropSavesInFlight -= 1;
  }
};

// Granular region-types save — the region TYPE definitions (sub-region
// descriptions, states, property schema, type-wide colours). Same reasoning as
// saveClasses above: these lived only in the whole-project PUT, which is
// last-write-wins, so a type edit could be clobbered by any other full-project
// save and never reached other clients until reload. Broadcasts 'project'.
// `known` = the type ids the saver's copy held before this change, so the
// server keeps a type someone else added meanwhile (and every field the copy
// did not carry). See mergeById in dbRoutes.
export const saveRegionTypes = (projectId, regionTypes, known = null) => putRegionTypeSettings(projectId, {
  regionTypes: regionTypes || [],
  ...(Array.isArray(known) ? { knownTypeIds: known } : {}),
});
// The headings the types are filed under (project.regionSections), through
// the same endpoint: never the whole-project save.
// The workspace's region settings (project.regionSettings): only the keys
// given are written, each checked by the server (REGION_SETTING_KEYS).
// One sub-region's meta on one type (e.g. "set as latest"): written onto the
// stored type by the server, never a whole type list from this copy.
export const saveSubRegionMeta = (projectId, typeId, subName, patch) => putRegionTypeSettings(projectId, {
  subRegionMeta: { typeId, subName, patch: patch || {} },
});
export const saveRegionSettings = (projectId, patch) => putRegionTypeSettings(projectId, { regionSettings: patch || {} });
export const saveRegionSections = (projectId, regionSections, known = null) => putRegionTypeSettings(projectId, {
  regionSections: regionSections || [],
  ...(Array.isArray(known) ? { knownSectionIds: known } : {}),
});
const putRegionTypeSettings = async (projectId, body) => {
  _docPropSavesInFlight += 1;
  try {
    const response = await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/region-types`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      let msg = `Server returned ${response.status}`;
      try { const j = await response.json(); msg = j.error || msg; } catch {}
      throw new Error(msg);
    }
    return response.json();
  } finally {
    _docPropSavesInFlight -= 1;
  }
};

// Project-level doc-property settings (auto-run detection, flag unassigned,
// …). Same reason as saveRegionTypes / saveClasses: these rode on the
// whole-project PUT, which is last-write-wins, so flipping one toggle from a
// minutes-old client copy silently reverted anything changed since. Server
// whitelists the keys and broadcasts 'project'.
/** The Document Objects settings (tag sync, tag warnings), one key at a time. */
export const saveObjectSettings = async (projectId, settings) => {
  const response = await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/object-settings`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ settings: settings || {} }),
  });
  if (!response.ok) {
    let msg = `Server returned ${response.status}`;
    try { const j = await response.json(); msg = j.error || msg; } catch { /* not JSON */ }
    throw new Error(msg);
  }
  return response.json();
};

export const saveProjectSettings = async (projectId, settings) => {
  _docPropSavesInFlight += 1;
  try {
    const response = await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/settings`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ settings: settings || {} }),
    });
    if (!response.ok) {
      let msg = `Server returned ${response.status}`;
      try { const j = await response.json(); msg = j.error || msg; } catch {}
      throw new Error(msg);
    }
    return response.json();
  } finally {
    _docPropSavesInFlight -= 1;
  }
};

// Doc-property templates — grouping/precedence, detection area, OCR engine,
// property add/rename/remove, create + delete. Same granular treatment.
export const saveDocPropTemplates = async (projectId, docPropTemplates, docPropTemplateTrash = undefined) => {
  _docPropSavesInFlight += 1;
  try {
    const response = await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/doc-prop-templates`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      // The trash rides along only when the caller has it; the server
      // keeps its own copy otherwise.
      body: JSON.stringify({ docPropTemplates: docPropTemplates || [], ...(Array.isArray(docPropTemplateTrash) ? { docPropTemplateTrash } : {}) }),
    });
    if (!response.ok) {
      let msg = `Server returned ${response.status}`;
      try { const j = await response.json(); msg = j.error || msg; } catch {}
      throw new Error(msg);
    }
    return response.json();
  } finally {
    _docPropSavesInFlight -= 1;
  }
};

// Title block templates — the workspace's own drawing-frame blocks, shared by
// everyone who creates a sheet here. Dedicated endpoint for the usual reason:
// the whole-project PUT is last-write-wins and would let an unrelated tab
// revert a block someone just authored.
export const saveTitleBlockTemplates = async (projectId, titleBlockTemplates) => {
  const response = await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/title-block-templates`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ titleBlockTemplates: titleBlockTemplates || [] }),
  });
  if (!response.ok) {
    let msg = `Server returned ${response.status}`;
    try { const j = await response.json(); msg = j.error || msg; } catch { /* non-JSON */ }
    throw new Error(msg);
  }
  return response.json();
};

/**
 * The workspace's reusable sheet blocks.
 *
 * Dedicated endpoint, never the whole-project PUT — a block authored here is
 * read wherever a sheet is created, and last-write-wins would let an unrelated
 * tab revert one somebody just saved.
 */
export const saveSheetBlocks = async (projectId, sheetBlocks) => {
  const response = await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/sheet-blocks`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sheetBlocks: sheetBlocks || [] }),
  });
  if (!response.ok) {
    let msg = `Server returned ${response.status}`;
    try { const j = await response.json(); msg = j.error || msg; } catch { /* non-JSON */ }
    throw new Error(msg);
  }
  return response.json();
};

export const saveProjectLineStyles = async (projectId, styles) => {
  const project = await getProject(projectId);
  if (!project) throw new Error('Project not found');
  
  project.lineStyles = styles;
  await saveProject(project);
};

// =============================================================================
// Backend API calls for files
// =============================================================================

// Upload PDF to backend
export const uploadPdfToBackend = async (file) => {
  const formData = new FormData();
  formData.append('pdf', file);

  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  // A Quick View's copy is only there for the bake to read; the server drops
  // an ephemeral upload after a couple of hours.
  const response = await fetch(`${BACKEND_URL}/api/upload${inQuickView() ? '?ephemeral=1' : ''}`, {
    method: 'POST',
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    body: formData
  });

  if (!response.ok) {
    let detail = '';
    try { const d = await response.json(); detail = d.error || ''; } catch {}
    throw new Error(detail || `Upload failed (${response.status})`);
  }

  return response.json();
};

// Get PDF from backend — fetches the whole file once and hands
// PDF.js a blob URL. The previous experiment (return the backend
// URL directly so PDF.js could use HTTP Range) turned out worse:
// SharePoint files are served by a Graph CDN with low RTT to the
// user, so range works great there, but our own backend lives a
// continent away from most users — every range request added a
// round-trip and zoom + page-flip felt noticeably laggier than
// SharePoint. One bulk fetch + in-memory rendering is faster here.
//
// We don't cache the blob URL: PDF.js keys its internal document
// cache by URL, and reusing a URL after the doc was destroyed used
// to hang the load. Cache-bust query param ensures every call
// resolves to a unique URL.
export const getPdfFromBackend = async (filename, sourceFolder) => {
  const dedupeKey = sourceFolder ? `pdf:sf:${sourceFolder}:${filename}` : `pdf:${filename}`;
  return dedupeRequest(dedupeKey, async () => {
    const cacheBust = `t=${Date.now()}`;
    const url = sourceFolder
      ? `${BACKEND_URL}/api/files/${encodeURIComponent(filename)}?sourceFolder=${encodeURIComponent(sourceFolder)}&${cacheBust}`
      : `${BACKEND_URL}/api/files/${encodeURIComponent(filename)}?${cacheBust}`;
    const response = await fetch(url);
    if (!response.ok) {
      let detail = '';
      try { const j = await response.json(); detail = j.error || j.message || ''; } catch {}
      throw new Error(`Failed to fetch PDF (${response.status}): ${detail || url}`);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  });
};

// Get a PDF blob URL for a project file object, routing based on how the
// file was added:
//
//   - isLocal + handleKey: File System Access API. Look up the
//     FileSystemFileHandle in IndexedDB, verify read permission (requesting
//     it if we're inside a user gesture), read bytes, wrap in a blob URL.
//     No backend round-trip — the PDF never leaves the user's disk.
//     Throws LocalFilePermissionError if the handle exists but permission
//     is 'prompt' / 'denied' and the call is outside a user gesture.
//
//   - otherwise: delegates to getPdfFromBackend using backendFilename /
//     sourceFolder for uploaded and SharePoint-cached files.
//
// Pass { requestPermission: true } on the initial open / retry-after-prompt
// path so the call counts as a user gesture and the browser will actually
// show the permission dialog. Background loads should leave it false.
/**
 * PDFs that exist only in memory — generated, never written anywhere.
 *
 * A new drawing has no disk handle and no backend copy, so neither branch below
 * can resolve it. Holding the bytes here lets it open in the workspace exactly
 * like any other document while remaining genuinely unsaved: nothing is
 * uploaded, and closing the tab loses it, which is the honest behaviour for a
 * file the user has not chosen to keep.
 *
 * Keyed by file id.
 */
const unsavedPdfs = new Map();

/** Register generated bytes so the file can be opened. */
export function registerUnsavedPdf(id, bytes) {
  unsavedPdfs.set(id, bytes);
}
export function getUnsavedPdf(id) {
  return unsavedPdfs.get(id) || null;
}
export function hasUnsavedPdf(id) {
  return unsavedPdfs.has(id);
}
export function forgetUnsavedPdf(id) {
  unsavedPdfs.delete(id);
}

export const getPdfUrlForFile = async (file, { requestPermission = false } = {}) => {
  if (!file) throw new Error('getPdfUrlForFile: file is required');

  // In-memory first: a generated document has neither a handle nor a backend
  // filename, so both branches below would fail on it.
  if (file.isUnsaved && unsavedPdfs.has(file.id)) {
    return URL.createObjectURL(new Blob([unsavedPdfs.get(file.id)], { type: 'application/pdf' }));
  }

  if (file.isLocal && file.handleKey) {
    const handle = await getHandle(file.handleKey);
    if (!handle) {
      throw new Error(`Local file handle missing for "${file.name}" — re-open from disk.`);
    }
    const granted = await verifyPermission(handle, { request: requestPermission });
    if (!granted) {
      throw new LocalFilePermissionError(file);
    }
    const blob = await handle.getFile();
    return URL.createObjectURL(blob);
  }

  const filename = file.backendFilename || file.name;
  try {
    return await getPdfFromBackend(filename, file.sourceFolder);
  } catch (err) {
    // SharePoint file that isn't cached on the backend yet (never opened this
    // session, cache evicted, or acted on without viewing first — e.g. picking
    // an un-opened file in Combine). The file server only reads local disk, so
    // pull it from SharePoint on-demand (same as OCR/detection do) and retry
    // once. Non-SP files / missing SP anchors fall through to the original error.
    if (file.isSharePoint && file.sharepoint?.driveId && file.sharepoint?.itemId) {
      const cached = await ensureSharepointCached(file);
      // A library document is read from the folder the library names
      // (a read copy per version, a working copy per holder).
      return await getPdfFromBackend(cached?.filename || filename, cached?.cacheDir || file.sourceFolder);
    }
    throw err;
  }
};

// Cascade-delete every per-file row keyed off any of the given
// filenames (objects, regions, OCR results, twins, punch items, twin
// graph edges). Used when a folder is deleted in the FileSidebar so
// the database doesn't accumulate orphaned data over time.
export const cleanupFolderFiles = async (projectId, filenames, anchors = {}) => {
  const fileIds = Array.isArray(anchors.fileIds) ? anchors.fileIds : [];
  const itemIds = Array.isArray(anchors.itemIds) ? anchors.itemIds : [];
  if (!projectId || ((!Array.isArray(filenames) || filenames.length === 0) && fileIds.length === 0 && itemIds.length === 0)) {
    return { success: true, removed: {} };
  }
  try {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(
      `${BACKEND_URL}/api/projects/${projectId}/folder-cleanup`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ filenames: filenames || [], fileIds, itemIds }),
      },
    );
    if (!response.ok) throw new Error(`Cleanup failed (${response.status})`);
    return await response.json();
  } catch (err) {
    console.error('[FOLDER-CLEANUP]', err);
    return { success: false, error: err.message };
  }
};

// Delete PDF from backend
export const deletePdfFromBackend = async (filename) => {
  // Invalidate cache
  const cachedUrl = cache.pdfUrls.get(filename);
  if (cachedUrl) {
    URL.revokeObjectURL(cachedUrl);
    cache.pdfUrls.delete(filename);
  }

  const response = await fetch(`${BACKEND_URL}/api/files/${encodeURIComponent(filename)}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error('Delete failed');
  }

  return response.json();
};

// Get list of all PDFs on backend
export const getBackendFiles = async () => {
  const response = await fetch(`${BACKEND_URL}/api/files`);
  
  if (!response.ok) {
    throw new Error('Failed to fetch file list');
  }

  const data = await response.json();
  return data.files;
};

// =============================================================================
// Source Folder API — open and list PDFs from any folder on disk
// =============================================================================

// List PDFs in a source folder path
export const listSourceFolder = async (folderPath) => {
  const response = await fetch(`${BACKEND_URL}/api/source-folder/list`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ folderPath })
  });
  
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || 'Failed to list source folder');
  }
  
  return response.json();
};

// Open native save-file dialog and return chosen path
export const pickSavePath = async (suggestedName) => {
  const response = await fetch(`${BACKEND_URL}/api/pick-save-path`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ suggestedName })
  });

  if (!response.ok) {
    throw new Error('Failed to open save dialog');
  }

  return response.json(); // { success, path } or { success: false, cancelled: true }
};

// =============================================================================
// Detector API calls
// =============================================================================

export const trainDetector = async (pdfFilename, boxes, multiOrientation = false, includeInverted = false, trainingMode = 'separate', modelType = 'object', projectId = null, addToExistingModel = null, sourceFolder = null) => {
  const body = {
    pdfFilename,
    boxes,
    multiOrientation,
    includeInverted,
    trainingMode,
    modelType,
    projectId,
    sourceFolder,
    // Runs as a job on the server; we ask for the result below.
    background: true,
  };
  
  // Only include addToModelId if it's set (to avoid backend errors if not supported)
  if (addToExistingModel) {
    body.addToModelId = addToExistingModel;  // Server expects addToModelId
  }
  
  // apiFetch so the Authorization header is attached (detector routes require
  // auth — a raw fetch 401s).
  const response = await apiFetch('/api/detector/train', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Training failed');
  }

  let result;
  if (response.status === 202) {
    // A background job: ask every few seconds until it finishes (up to 30
    // minutes; the detector can take a while on a cold start).
    const { jobId } = await response.json();
    const until = Date.now() + 30 * 60 * 1000;
    for (;;) {
      await new Promise(r => setTimeout(r, 3000));
      let r;
      try { r = await apiFetch(`/api/detector/train/jobs/${encodeURIComponent(jobId)}`); }
      catch { if (Date.now() > until) throw new Error('Training is taking too long to answer.'); continue; }
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || 'Training failed');
      if (body.status === 'done') { result = body.result; break; }
      if (body.status === 'error') throw new Error(body.error || 'Training failed');
      if (Date.now() > until) throw new Error('Training is taking too long to answer. It may still finish: the model list updates when it does.');
    }
  } else {
    result = await response.json();
  }

  // Invalidate models cache after training
  invalidateCache(cache.models, projectId || 'global');

  return result;
};

/**
 * Make sure a SharePoint-backed PDF is sitting on Railway's local cache
 * before kicking off detection on it. Each "All Documents" run hits a
 * mix of files: most are already cached because the user's been opening
 * them in the viewer; the rest have never been pulled and would 404
 * inside runDetection. The backend /ensure-cached route is a no-op when
 * the eTag-tagged copy is already on disk, otherwise it synchronously
 * downloads from SharePoint, then returns.
 *
 * Returns null for non-SP files (caller should skip the call). Throws
 * if the SP fetch itself fails so the loop can record it as a per-file
 * failure and continue.
 */
export const ensureSharepointCached = async (file) => {
  if (!file?.isSharePoint) return null;
  const driveId = file.sharepoint?.driveId;
  const itemId = file.sharepoint?.itemId;
  if (!driveId || !itemId) return null;
  const filename = file.backendFilename || file.name;
  // Through the SharePoint fetch wrapper, never apiFetch: the SharePoint
  // routes take the pidly user from X-Pidly-Token and the site binding
  // from X-Pidly-Project-Id, and apiFetch sends neither, so this call
  // 401'd (and, on a connected project, the backend tried the pidly JWT
  // as a Microsoft token). Auto-detection, the object finder, smart links
  // and the open-file fallback all warm the cache through here.
  return ensureFileCached(driveId, itemId, filename);
};

// ── Shared, refresh-proof operation status (detect / ocr / smartlinks) ──
// Creates a backend Job row so EVERY user (and the same user after a refresh)
// sees the operation running via the existing job WS + DetectionStatusWidget.
// All best-effort: status must never block or break the actual operation.
export const createJobStatus = async (projectId, type, payload = {}) => {
  if (!projectId) { console.warn(`[jobStatus] ${type}: no projectId — skipping shared status`); return null; }
  try {
    const res = await apiFetch(`/api/projects/${projectId}/jobs`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, payload }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.job?.id) {
      console.warn(`[jobStatus] ${type}: create failed (${res.status}) — ${data?.error || 'no job id'}. Backend may not be redeployed yet.`);
      return null;
    }
    return data.job.id;
  } catch (e) { console.warn(`[jobStatus] ${type}: create errored`, e?.message || e); return null; }
};
export const updateJobStatus = async (projectId, jobId, patch) => {
  if (!projectId || !jobId) return;
  try {
    await apiFetch(`/api/projects/${projectId}/jobs/${jobId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
  } catch { /* status is best-effort */ }
};

export const runDetection = async (pdfFilename, options) => {
  console.log('[runDetection] detectionArea:', options.detectionArea ? JSON.stringify(options.detectionArea) : 'none');

  // Per-file abort-on-timeout. Aligned with Modal's function timeout
  // (10 min, see modal_app.py) — the previous 4-minute ceiling was
  // killing requests the backend was still actively running, which
  // showed up as false "Detection timed out" failures on big PDFs or
  // cold-started containers. The retry layer in useBatchDetection
  // catches transient aborts and re-tries, so finite-but-generous is
  // the right shape here.
  const ctrl = new AbortController();
  const timeoutId = setTimeout(() => ctrl.abort(), 10 * 60 * 1000);

  let response;
  try {
    // apiFetch (not raw fetch) so the Authorization header is attached — the
    // detector route requires auth and a raw fetch 401s ("Authentication
    // required"), which surfaced as every page returning "No detections".
    response = await apiFetch('/api/detector/detect', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: ctrl.signal,
      body: JSON.stringify({
        pdfFilename,
        confidence: options.confidence,
        selectedModels: options.selectedModels,
        enableOCR: options.enableOCR,
        ocrPadding: options.ocrPadding || 1.0,
        classPatterns: options.classPatterns,
        // Global format template (for Smart Links)
        formatTemplate: options.formatTemplate || null,
        // Per-class settings (confidence, OCR enable, and format per class)
        perClassSettings: options.perClassSettings || null,
        // Project ID — needed to load models from the right per-project dir,
        // and also used for incremental object saves (unless saveToObjects=false).
        projectId: options.projectId || null,
        // When false, detections aren't written to the project object store.
        // Doc-prop extraction passes this to avoid polluting the objects list.
        saveToObjects: options.saveToObjects !== false,
        // Pages to detect on (array of page numbers, null = all pages)
        pages: options.pages || null,
        // Source folder for non-uploaded PDFs
        sourceFolder: options.sourceFolder || null,
        // SharePoint locator so the backend can pull the PDF from SharePoint
        // before detecting (parity with OCR) — makes detection reliable on ANY
        // document, not just one whose cache was pre-warmed.
        sharepoint: options.sharepoint || null,
        // Detection area (normalized bbox to crop before inference)
        detectionArea: options.detectionArea || null,
        // When true, the detector reads each object's tag off its crop with
        // Claude vision (overrides PaddleOCR for the tag).
        aiTagFromCrop: options.aiTagFromCrop || false,
        // OCR engine for sub-region reads — 'paddle' (default) or
        // 'claude-haiku'. Templates opt in by setting it on themselves;
        // the doc-prop extraction call below reads from the template
        // and threads it through here.
        ocrEngine: options.ocrEngine || null,
      })
    });
  } catch (err) {
    clearTimeout(timeoutId);
    if (err?.name === 'AbortError') {
      throw new Error(`Detection timed out for ${pdfFilename} (10 min). Skipping.`);
    }
    throw err;
  }
  clearTimeout(timeoutId);

  if (!response.ok) {
    let error = {};
    try { error = await response.json(); } catch {}
    throw new Error(error.error || `Detection failed (${response.status})`);
  }

  const result = await response.json();
  console.log('[runDetection] Response:', JSON.stringify(result.detections?.[0]?.subclassValues || 'NO subclassValues'));
  return result;
};

// Run full-page OCR to extract ALL text with bounding boxes
// Useful for finding pipe tags and other text not inside detected symbols
//
// Auth: uses apiFetch so the Authorization header is attached. Without
// it the backend's requireProjectPermFromBody middleware bails on
// req.user being undefined and the OCR run silently 401s.
// projectId is optional but recommended — it lets the middleware
// short-circuit personal-project creator checks and runs the matrix
// for workspace projects.
// ── Workspace-shared stamp libraries ────────────────────────────────────────
export const fetchSharedStampLibraries = async (projectId) => {
  const r = await apiFetch(`${BACKEND_URL}/api/projects/${projectId}/shared-stamps`);
  if (!r.ok) throw new Error('Failed to load shared stamps');
  const body = await r.json();
  return body.libraries || [];
};

export const saveSharedStampLibrary = async (projectId, { id, title, stamps }) => {
  const r = await apiFetch(`${BACKEND_URL}/api/projects/${projectId}/shared-stamps/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title, stamps }),
  });
  if (!r.ok) {
    let msg = 'Failed to save shared stamps';
    try { const b = await r.json(); if (b?.error) msg = b.error; } catch { /* body */ }
    throw new Error(msg);
  }
  return (await r.json()).library;
};

export const deleteSharedStampLibrary = async (projectId, id) => {
  const r = await apiFetch(`${BACKEND_URL}/api/projects/${projectId}/shared-stamps/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error('Failed to delete shared stamps');
};

export const runFullPageOcr = async (pdfFilename, page = 1, dpi = 300, sourceFolder = null, detectionArea = null, enhancedDetection = false, ocrEngine = 'tesseract', projectId = null) => {
  const response = await apiFetch(`${BACKEND_URL}/api/ocr/fullpage`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      projectId,
      pdfFilename,
      page,
      dpi,
      sourceFolder,
      detectionArea,
      enhancedDetection,
      ocrEngine,
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Full-page OCR failed');
  }

  return response.json();
};

// AI verify (standalone): send a page's OCR items; backend picks the uncertain
// ones, Claude re-reads them, returns corrections keyed to the original index.
export const verifyOcrResults = async (pdfFilename, page, items, { model, sourceFolder, projectId, sharepoint, dpi } = {}) => {
  const response = await apiFetch(`${BACKEND_URL}/api/ocr/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pdfFilename, page, items, model, sourceFolder: sourceFolder || null, projectId, sharepoint: sharepoint || null, dpi }),
  });
  if (!response.ok) {
    let msg = 'AI verify failed';
    try { const b = await response.json(); if (b?.error) msg = b.error; } catch {}
    throw new Error(msg);
  }
  return response.json();
};

// AI gap-fill (standalone): send existing items; backend returns genuinely-new
// text it recovered from the residual regions.
export const gapfillOcrResults = async (pdfFilename, page, existing, { model, sourceFolder, projectId, sharepoint, dpi } = {}) => {
  const response = await apiFetch(`${BACKEND_URL}/api/ocr/gapfill`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pdfFilename, page, existing, model, sourceFolder: sourceFolder || null, projectId, sharepoint: sharepoint || null, dpi }),
  });
  if (!response.ok) {
    let msg = 'AI gap-fill failed';
    try { const b = await response.json(); if (b?.error) msg = b.error; } catch {}
    throw new Error(msg);
  }
  return response.json();
};

export const getModels = async (projectId = null) => {
  const cacheKey = projectId || 'global';
  
  // Check cache
  const cached = getCached(cache.models, cacheKey);
  if (cached) {
    return cached;
  }

  // Dedupe concurrent requests
  return dedupeRequest(`models:${cacheKey}`, async () => {
    const url = projectId
      ? `/api/models/list?projectId=${encodeURIComponent(projectId)}`
      : `/api/models/list`;
    const response = await apiFetch(url);
    
    if (!response.ok) {
      throw new Error('Failed to fetch models');
    }

    const data = await response.json();
    const models = data.models;
    
    // Cache the result
    setCache(cache.models, cacheKey, models);
    
    return models;
  });
};

// Another tab or person changed a model: the next read must not come from
// this tab's 30s cache.
export const invalidateModelsCache = () => { cache.models.clear(); };

export const deleteModel = async (modelId, projectId) => {
  const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  const response = await apiFetch(`/api/models/${encodeURIComponent(modelId)}${qs}`, {
    method: 'DELETE'
  });

  if (!response.ok) {
    throw new Error('Failed to delete model');
  }

  // Invalidate all model caches (we don't know which project this belongs to)
  cache.models.clear();

  return response.json();
};

// Add negative examples to a model's metadata
export const addNegativeExamples = async (modelId, negativeExamples, projectId = null) => {
  // apiFetch (not raw fetch) so the Authorization header rides along, and
  // pass projectId so the backend can find the model in the detector's
  // project-scoped store on the Modal volume — without it the write lands
  // in the wrong filesystem and the negatives never reach detection.
  const response = await apiFetch(`/api/models/${encodeURIComponent(modelId)}/negatives`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ negativeExamples, projectId })
  });

  if (!response.ok) {
    let msg = `Server returned ${response.status}`;
    try { const err = await response.json(); msg = err.error || msg; } catch { /* non-JSON response */ }
    throw new Error(msg);
  }

  cache.models.clear();
  return response.json();
};

// Save a model's settings. The server merges what is sent onto the model and
// never takes the example lists from here, so send only what changed:
// updateModelSettings does that. (Signed in, and with this tab's client id,
// so the save does not echo back to the tab that made it.)
export const saveModel = async (model) => {
  const response = await apiFetch(`/api/models/save`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(model)
  });

  if (!response.ok) {
    throw new Error('Failed to save model');
  }

  // Invalidate models cache
  cache.models.clear();

  return response.json();
};

/**
 * Remove one example from a model. The server rebuilds the model from the
 * examples it keeps (each on its own drawing), so the model's id changes;
 * resolves { newModelId } or { deleted: true } when it was the last one.
 */
export const removeModelExample = async (modelId, exampleId, projectId) => {
  const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  const response = await apiFetch(`/api/models/${encodeURIComponent(modelId)}/examples/${encodeURIComponent(exampleId)}${qs}`, { method: 'DELETE' });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `Server returned ${response.status}`);
  cache.models.clear();
  return body;
};

/**
 * Merge a class's models into ONE (one model per class): every example and
 * every "not this" of each goes into a new model, the old ones are removed.
 * Resolves { newModelId, examples, merged }.
 */
export const mergeModels = async (modelIds, projectId) => {
  const response = await apiFetch('/api/models/merge', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ modelIds, projectId }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `Server returned ${response.status}`);
  cache.models.clear();
  return body;
};

/** Change some of a model's settings: only `patch` is sent, with the model's id and project. */
export const updateModelSettings = (model, patch, projectId = null) =>
  saveModel({ id: model.id, projectId: model.projectId || projectId || undefined, ...patch });

// ── A model travels between workspaces as ONE zip (2026-11-03) ──
// The detector writes the model's own files (.pkl with every template it was
// taught, its metadata) and a signed manifest; the browser adds
// `pidly-classes.json`, the set-up of the classes it finds (colour, tag
// format, sub-classes, picture), so the workspace that imports it gets the
// classes as well as the model and it detects with no retraining.
const CLASS_KEYS_DROPPED = new Set(['id', 'parentId', 'objects', 'count', 'created', 'updated', 'modified']);
const classDefOf = (cls, all) => {
  const out = {};
  for (const [k, v] of Object.entries(cls || {})) if (!CLASS_KEYS_DROPPED.has(k)) out[k] = v;
  out.subclasses = (all || []).filter(c => c && c.parentId && c.parentId === cls.id).map(c => classDefOf(c, all));
  return out;
};

/** Download models (one, or several) as a zip another workspace can import. */
export const exportModelsFile = async ({ models, projectId, classes = [], fileName }) => {
  const list = (models || []).filter(Boolean);
  if (!list.length) throw new Error('No model to download');
  const q = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  const url = list.length === 1
    ? `/api/models/export/${encodeURIComponent(list[0].id || list[0].modelId)}${q}`
    : `/api/models/export${q}`;
  const response = await apiFetch(url);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'The download failed');
  }
  const { default: JSZip } = await import('jszip');
  const zip = await JSZip.loadAsync(await response.blob());
  const names = new Set();
  for (const m of list) for (const n of [m.className, ...(Array.isArray(m.classes) ? m.classes : [])]) if (n) names.add(n);
  const top = (classes || []).filter(c => c && !c.parentId && names.has(c.name));
  zip.file('pidly-classes.json', JSON.stringify({ classes: top.map(c => classDefOf(c, classes)) }, null, 2));
  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  const first = list[0];
  const safe = String(fileName || (list.length === 1 ? (first.name || first.className || first.id) : 'models')).replace(/[^a-zA-Z0-9_-]+/g, '_');
  link.download = `pidly_model_${safe}_${new Date().toISOString().slice(0, 10)}.zip`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
};

// Kept for the older Models page: one model, through the same download.
export const exportSingleModel = async (modelId, className, projectId = null, classes = []) =>
  exportModelsFile({ models: [{ id: modelId, className }], projectId, classes });

/** What a model zip holds, read in the browser before anything is sent:
 *  { models: [{ id, name, className, classes, modelType }], classes, signed }. */
export const readModelsFile = async (file) => {
  const { default: JSZip } = await import('jszip');
  let zip;
  try { zip = await JSZip.loadAsync(file); } catch { throw new Error('That is not a zip file.'); }
  const manifestFile = zip.file('pidly-model.json');
  let manifest = null;
  if (manifestFile) {
    try { manifest = JSON.parse(await manifestFile.async('string')); } catch { throw new Error('The model file is damaged.'); }
  }
  let models = Array.isArray(manifest?.models) ? manifest.models : null;
  if (!models) {
    // An older export (before the manifest): read each model's metadata.
    models = [];
    for (const name of Object.keys(zip.files).filter(n => n.endsWith('.pkl') && !n.includes('/'))) {
      const id = name.slice(0, -4);
      let meta = {};
      try { meta = JSON.parse(await zip.file(`${id}_metadata.json`)?.async('string') || '{}'); } catch { /* no metadata */ }
      models.push({ id, name: meta.name, className: meta.className, classes: meta.classes || (meta.className ? [meta.className] : []), modelType: meta.modelType });
    }
  }
  if (!models.length) throw new Error('There is no model in that file.');
  // Each model's own settings (confidence, tag format, how the tag is
  // found, read direction...) are in its metadata: shown before import.
  models = await Promise.all(models.map(async (m) => {
    let meta = {};
    try { meta = JSON.parse(await zip.file(`${m.id}_metadata.json`)?.async('string') || '{}'); } catch { /* the manifest's facts */ }
    return { ...m, ...meta, id: m.id };
  }));
  let classes = [];
  try { classes = JSON.parse(await zip.file('pidly-classes.json')?.async('string') || '{}').classes || []; } catch { /* none carried */ }
  return { models, classes, signed: !!manifest?.signed, sourceProjectId: manifest?.sourceProjectId || null };
};

/** Send a model zip to a workspace. `replace` overwrites a model already there. */
export const importModelsFile = async (file, projectId, { replace = false } = {}) => {
  const fd = new FormData();
  fd.append('file', file);
  if (projectId) fd.append('projectId', projectId);
  const q = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  const response = await apiFetch(`${replace ? '/api/models/import-overwrite' : '/api/models/import'}${q}`, { method: 'POST', body: fd });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `The server answered ${response.status}`);
  cache.models.clear();
  return body;
};

// Get thumbnail for an object
export const getThumbnail = async (filename, page, bbox, rotation = 0, inverted = false, sourceFolder = null) => {
  const response = await fetch(`${BACKEND_URL}/api/thumbnail`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ filename, page, bbox, rotation, inverted, sourceFolder })
  });

  if (!response.ok) {
    throw new Error('Failed to get thumbnail');
  }

  const data = await response.json();
  return data.thumbnail;
};

// Capture region from PDF at 300 DPI (for subclass region definition)
// Returns { image: base64, width: pixels, height: pixels, dpi: 300 }
export const captureRegion = async (filename, page, bbox, sourceFolder = null) => {
  const response = await fetch(`${BACKEND_URL}/api/capture-region`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ filename, page, bbox, sourceFolder })
  });

  if (!response.ok) {
    throw new Error('Failed to capture region');
  }

  return response.json();
};

export const vectoriseRegion = async (filename, sourceFolder, pageIndex, region, settings, excludeRects) => {
  const response = await fetch(`${BACKEND_URL}/api/vectorise`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      filename,
      sourceFolder: sourceFolder || null,
      pageIndex,
      region: {
        x: Math.min(region.startX, region.endX),
        y: Math.min(region.startY, region.endY),
        width: Math.abs(region.endX - region.startX),
        height: Math.abs(region.endY - region.startY),
      },
      settings: settings || {},
      // Normalised 0->1 full-page symbol bboxes to mask out before tracing.
      excludeRects: Array.isArray(excludeRects) ? excludeRects : [],
    }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || 'Vectorisation failed');
  }

  return response.json();
};

// =============================================================================
// Objects Storage API calls - WITH CACHING
// =============================================================================

// Get detected objects for a project from backend file
export const getObjectsFromBackend = async (projectId, forceRefresh = false) => {
  if (!forceRefresh) {
    const cached = getCached(cache.objects, projectId);
    if (cached) {
      return cached;
    }
  }

  // Dedupe concurrent requests
  return dedupeRequest(`objects:${projectId}`, async () => {
    const response = await apiFetch(`/api/objects/${encodeURIComponent(projectId)}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch objects');
    }

    const data = await response.json();
    const objects = data.objects || [];
    
    // Cache the result
    setCache(cache.objects, projectId, objects);
    
    return objects;
  });
};

// Save detected objects for a project to backend file.
//
// SAFETY GUARD: Refuses to persist an empty array unless the caller explicitly
// opts in via `{ allowEmpty: true }`. This protects against cross-device race
// conditions where a second session (e.g. phone) loads with stale/failed
// auth, ends up with empty in-memory state, and auto-saves that empty state
// back to Postgres — wiping imported data for all devices.
//
// If you really need to clear all objects for a project (e.g. explicit bulk
// delete), call with: saveObjectsToBackend(projectId, [], { allowEmpty: true })
export const saveObjectsToBackend = async (projectId, objects, options = {}) => {
  const { allowEmpty = false, suppressClassRename = null, suppressBulkGroup = null } = options;

  if (!Array.isArray(objects)) {
    console.error('[saveObjectsToBackend] Refusing to save: objects must be an array, got', typeof objects);
    throw new Error('Objects must be an array');
  }

  if (objects.length === 0 && !allowEmpty) {
    console.warn('[saveObjectsToBackend] Refusing to save empty objects array without allowEmpty flag (likely a stale-state race). Skipping.');
    return { success: true, skipped: true, reason: 'empty-guard' };
  }

  // Invalidate cache before saving
  invalidateCache(cache.objects, projectId);

  // suppressClassRename: { from, to } tells the backend not to
  // emit one object.classChange audit entry per affected object
  // when the underlying cause is a class rename (the project
  // save already wrote a single class.rename entry).
  // suppressBulkGroup: { groupId, groupName, action, count }
  // collapses N per-object groupIds changes into a single
  // group.bulkAssign / group.bulkClear audit entry.
  const body = { objects };
  if (suppressClassRename) body.suppressClassRename = suppressClassRename;
  if (suppressBulkGroup) body.suppressBulkGroup = suppressBulkGroup;
  // The backend reads allowEmpty from the QUERY string (not the body), so a
  // legitimate "delete every object" save must carry it there or the
  // server-side empty-guard silently refuses it and the deletion never sticks.
  const qs = allowEmpty ? '?allowEmpty=true' : '';
  const response = await apiFetch(`/api/objects/${encodeURIComponent(projectId)}${qs}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    throw new Error('Failed to save objects');
  }

  // Update cache with new data
  setCache(cache.objects, projectId, objects);

  return response.json();
};

// Patch objects for a project — partial update of specific objects by id.
// Much lighter than saveObjectsToBackend when only a few objects changed
// (e.g. single-object edit). Sends just the changed/deleted entries.
// changes: { update?: [partialObject], delete?: [id] }
export const patchObjectsBackend = async (projectId, changes) => {
  const update = Array.isArray(changes?.update) ? changes.update : [];
  const deleteIds = Array.isArray(changes?.delete) ? changes.delete : [];
  if (update.length === 0 && deleteIds.length === 0) {
    return { success: true, skipped: true };
  }

  // Invalidate cache — fresh data will be re-fetched on next read
  invalidateCache(cache.objects, projectId);

  // The delta is additive/by-id, so retrying it is idempotent. Retry on 409
  // (server couldn't win its compare-and-swap under heavy contention — common
  // when the detection worker is flushing at the same time) so a transient
  // clash doesn't drop the write (and silently lose objects).
  let lastErr = null;
  for (let attempt = 0; attempt < 5; attempt++) {
    const response = await apiFetch(`/api/objects/${encodeURIComponent(projectId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      // suppressClassRename: { from, to } — these updates are the downstream
      // of one class rename; the route writes no per-object classChange row.
      body: JSON.stringify({ update, delete: deleteIds, ...(changes?.suppressClassRename ? { suppressClassRename: changes.suppressClassRename } : {}), ...(changes?.suppressBulkGroup ? { suppressBulkGroup: changes.suppressBulkGroup } : {}), ...(changes?.system ? { system: true } : {}) }),
    });
    if (response.ok) return response.json();
    const data = await response.json().catch(() => ({}));
    const err = new Error(data.error || 'Failed to patch objects');
    err.status = response.status;
    // 409 = lost compare-and-swap; 503 = transient auth/DB hiccup under load.
    // Both are safe to replay (the delta is idempotent by id).
    if ((response.status === 409 || response.status === 503) && attempt < 4) {
      lastErr = err;
      await new Promise(r => setTimeout(r, 150 * (attempt + 1)));
      continue;
    }
    throw err;
  }
  throw lastErr || new Error('Failed to patch objects');
};

// Delete objects file for a project
export const deleteObjectsFromBackend = async (projectId) => {
  // Invalidate cache
  invalidateCache(cache.objects, projectId);

  const response = await apiFetch(`/api/objects/${encodeURIComponent(projectId)}`, {
    method: 'DELETE'
  });

  if (!response.ok) {
    throw new Error('Failed to delete objects');
  }

  return response.json();
};

// =============================================================================
// Model Templates & Examples API calls
// =============================================================================

// Get templates for a model (base64 images)
export const getModelTemplates = async (modelId) => {
  const response = await fetch(`${BACKEND_URL}/api/models/${encodeURIComponent(modelId)}/templates`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch templates');
  }

  const data = await response.json();
  return data.templates || [];
};

// Get examples for a model (original RGB images before preprocessing)
export const getModelExamples = async (modelId) => {
  const response = await fetch(`${BACKEND_URL}/api/models/${encodeURIComponent(modelId)}/examples`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch examples');
  }

  const data = await response.json();
  // Fall back to templates for older models that don't have examples stored
  return data.examples || data.templates || [];
};


// =============================================================================
// Local File Helpers
// =============================================================================

// Check if File System Access API is supported
export const isFileSystemAccessSupported = () => {
  return typeof window !== 'undefined' && 'showDirectoryPicker' in window;
};

// Get PDF URL - handles backend files, local blob URLs, and source folder files
export const getPdfUrl = async (file) => {
  if (file.blobUrl) {
    // Local file - already has blob URL
    return file.blobUrl;
  } else if (file.sourceFolder) {
    // Source folder file — served via backend with sourceFolder param
    return getPdfFromBackend(file.backendFilename || file.name, file.sourceFolder);
  } else if (file.backendFilename) {
    // Backend file - fetch from server (with caching)
    return getPdfFromBackend(file.backendFilename);
  } else {
    throw new Error('Invalid file: no blobUrl, sourceFolder, or backendFilename');
  }
};

// Create blob URL from File object
export const createBlobUrl = (fileObject) => {
  return URL.createObjectURL(fileObject);
};

// Revoke blob URL to free memory
export const revokeBlobUrl = (blobUrl) => {
  if (blobUrl && blobUrl.startsWith('blob:')) {
    URL.revokeObjectURL(blobUrl);
  }
};

// =============================================================================
// Regions Storage API calls - WITH CACHING
// =============================================================================

// Get drawn regions for a project from backend file
export const getRegionsFromBackend = async (projectId, forceRefresh = false) => {
  if (!forceRefresh) {
    const cached = getCached(cache.regions, projectId);
    if (cached) {
      return cached;
    }
  }

  // Dedupe concurrent requests
  return dedupeRequest(`regions:${projectId}`, async () => {
    const response = await apiFetch(`/api/regions/${encodeURIComponent(projectId)}`);
    
    if (!response.ok) {
      if (response.status === 404) {
        // Cache empty result too
        setCache(cache.regions, projectId, []);
        regionRecordsCache.set(projectId, []);
        regionMarkupsCache.set(projectId, []);
        return []; // No regions file yet
      }
      throw new Error('Failed to fetch regions');
    }

    const data = await response.json();
    // The regions store holds two kinds of thing (app/regions-as-markups.md):
    // the old region SHAPES, which every reader of this function knows, and
    // region MARKUPS, which only the markup bridge (hooks/useRegionMarkups)
    // may touch. They are split here, once, so no shape reader, and no
    // whole-list save built from one, ever sees a region markup.
    const all = Array.isArray(data.regions) ? data.regions : [];
    // The OLD region shapes are DROPPED (phase 6, "start fresh"): a region
    // is a markup now. They are no longer read by anything; the next shapes
    // save (a whole-list PUT of none) removes them from the table, and the
    // server keeps every region markup through it.
    const regions = [];
    regionMarkupsCache.set(projectId, all.filter(isRegionMarkup));
    
    // Cache the result
    setCache(cache.regions, projectId, regions);
    // The sub-region RECORDS arrive on the same read (app/region-records.md).
    regionRecordsCache.set(projectId, Array.isArray(data.records) ? data.records : []);
    
    return regions;
  });
};

// Sub-region records, kept from the last regions read. getRegionsFromBackend
// callers that do not care about records are unchanged.
const regionRecordsCache = new Map();
// Region MARKUPS from the last regions read (never in the shapes list).
const regionMarkupsCache = new Map();

/**
 * Delete every region markup on these files (a purged document takes its
 * regions with it). By id through the PATCH: a whole-list save of shapes
 * never touches region markups. Resolves the number deleted.
 */
export const deleteRegionMarkupsForFiles = async (projectId, filenames) => {
  const names = new Set((filenames || []).filter(Boolean));
  if (!names.size) return 0;
  const list = await getRegionMarkups(projectId, true);
  const ids = list.filter(m => names.has(m.filename)).map(m => m.id);
  if (ids.length) await patchRegionsBackend(projectId, { update: [], delete: ids });
  return ids.length;
};

/**
 * The project's region markups: the closed markups that are regions, kept in
 * the regions store, never in a PDF. Written only through patchRegionsBackend
 * (by id; the server keeps them through a whole-list PUT of shapes).
 */
export const getRegionMarkups = async (projectId, forceRefresh = false) => {
  if (forceRefresh || !regionMarkupsCache.has(projectId)) await getRegionsFromBackend(projectId, true);
  return regionMarkupsCache.get(projectId) || [];
};

/** { regions, records } for a project: the drawn shapes and the sub-region records. */
export const getRegionsWithRecords = async (projectId, forceRefresh = false) => {
  let regions = await getRegionsFromBackend(projectId, forceRefresh);
  // A cached regions list from before records were read: read both again.
  if (!regionRecordsCache.has(projectId)) regions = await getRegionsFromBackend(projectId, true);
  return { regions, records: regionRecordsCache.get(projectId) || [] };
};

// Save drawn regions for a project to backend file
export const saveRegionsToBackend = async (projectId, regions) => {
  // Invalidate cache before saving
  invalidateCache(cache.regions, projectId);

  const response = await apiFetch(`/api/regions/${encodeURIComponent(projectId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ regions })
  });

  if (!response.ok) {
    throw new Error('Failed to save regions');
  }

  // Update cache with new data
  setCache(cache.regions, projectId, regions);

  return response.json();
};

// Patch regions — partial merge of specific regions by id (delta auto-save).
// Sends only the changed/deleted entries; the server merges them with
// optimistic concurrency. changes: { update?: [region], delete?: [id] }
export const patchRegionsBackend = async (projectId, changes) => {
  const update = Array.isArray(changes?.update) ? changes.update : [];
  const deleteIds = Array.isArray(changes?.delete) ? changes.delete : [];
  // Sub-region records change in the same write (app/region-records.md).
  const recordUpdate = Array.isArray(changes?.recordUpdate) ? changes.recordUpdate : [];
  const recordDelete = Array.isArray(changes?.recordDelete) ? changes.recordDelete : [];
  if (update.length === 0 && deleteIds.length === 0 && recordUpdate.length === 0 && recordDelete.length === 0) {
    return { success: true, skipped: true };
  }
  invalidateCache(cache.regions, projectId);
  regionRecordsCache.delete(projectId);
  const response = await apiFetch(`/api/regions/${encodeURIComponent(projectId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ update, delete: deleteIds, ...(recordUpdate.length ? { recordUpdate } : {}), ...(recordDelete.length ? { recordDelete } : {}) }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const err = new Error(data.error || 'Failed to patch regions');
    err.status = response.status; // 409 = CAS contention; caller may reconcile
    throw err;
  }
  return response.json();
};

// ── OCR results persistence (per-project, shared across users) ──

// Returns { data, version } — version drives the compare-and-swap on save.
export const getOcrFromBackend = async (projectId) => {
  try {
    const response = await apiFetch(`/api/ocr/${encodeURIComponent(projectId)}`);
    if (!response.ok) return { data: {}, version: 0 };
    const body = await response.json();
    return { data: body?.data || {}, version: Number.isInteger(body?.version) ? body.version : 0 };
  } catch (err) {
    console.warn('[OCR] Load failed:', err.message);
    return { data: {}, version: 0 };
  }
};

// Which files have OCR, how much and with which engine, without the text.
export const getOcrSummaryFromBackend = async (projectId) => {
  try {
    const response = await apiFetch(`/api/ocr/${encodeURIComponent(projectId)}?summary=1`);
    if (!response.ok) return { files: {}, version: 0 };
    const body = await response.json();
    return { files: body?.files || {}, version: Number.isInteger(body?.version) ? body.version : 0 };
  } catch (err) {
    console.warn('[OCR] Summary load failed:', err.message);
    return { files: {}, version: 0 };
  }
};

// The OCR of SOME files (a drawing that opens, the files of a run), in
// chunks so the URL stays short. Same { data, version } shape as the whole read.
export const getOcrFilesFromBackend = async (projectId, files) => {
  const names = [...new Set((files || []).filter(Boolean))];
  const out = { data: { resultsByFile: {}, dpiByFile: {}, engineByFile: {} }, version: 0 };
  for (let i = 0; i < names.length; i += 25) {
    const chunk = names.slice(i, i + 25);
    try {
      const response = await apiFetch(`/api/ocr/${encodeURIComponent(projectId)}?files=${chunk.map(encodeURIComponent).join(',')}`);
      if (!response.ok) continue;
      const body = await response.json();
      Object.assign(out.data.resultsByFile, body?.data?.resultsByFile || {});
      Object.assign(out.data.dpiByFile, body?.data?.dpiByFile || {});
      Object.assign(out.data.engineByFile, body?.data?.engineByFile || {});
      if (Number.isInteger(body?.version)) out.version = body.version;
    } catch (err) {
      console.warn('[OCR] Load failed:', err.message);
    }
  }
  return out;
};

// Remove every OCR result of a project (the "clear project" action).
export const deleteProjectOcr = async (projectId) => {
  try {
    const response = await apiFetch(`/api/ocr/${encodeURIComponent(projectId)}`, { method: 'DELETE' });
    return response.ok;
  } catch (err) {
    console.warn('[OCR] Delete failed:', err.message);
    return false;
  }
};

// Sends the version we last saw; returns { version } (the new server version) so
// the caller can keep its token current. Returns null on failure.
export const saveOcrToBackend = async (projectId, data, version, opts = {}) => {
  try {
    const response = await apiFetch(`/api/ocr/${encodeURIComponent(projectId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data, version, removed: opts.removed, authoritative: opts.authoritative }),
    });
    if (!response.ok) throw new Error('Failed to save OCR data');
    return response.json();
  } catch (err) {
    console.warn('[OCR] Save failed:', err.message);
    return null;
  }
};

// Reliable page count for OCR scoping — resolved by the backend (which pulls
// from SharePoint if needed), so we never fall back to guessing "50 pages" and
// OCR non-existent pages. Throws on failure (caller decides the safe fallback).
export const getPdfPageCount = async (filename, { sourceFolder, projectId, sharepoint } = {}) => {
  const p = new URLSearchParams();
  if (sourceFolder) p.set('sourceFolder', sourceFolder);
  if (projectId) p.set('projectId', projectId);
  if (sharepoint?.driveId && sharepoint?.itemId) { p.set('driveId', sharepoint.driveId); p.set('itemId', sharepoint.itemId); }
  const qs = p.toString();
  const res = await apiFetch(`/api/pdf-pagecount/${encodeURIComponent(filename)}${qs ? `?${qs}` : ''}`);
  if (!res.ok) throw new Error(`page count ${res.status}`);
  const d = await res.json();
  if (!Number.isInteger(d.pageCount) || d.pageCount < 1) throw new Error('bad page count');
  return d.pageCount;
};

// Each page's shown size in points ({ w, h, r }: crop box turned by /Rotate),
// read by the server without the file being opened in the browser. Throws on
// failure; a caller with the bytes can read them itself.
export const getPdfPageSizes = async (filename, { sourceFolder, projectId, sharepoint } = {}) => {
  const p = new URLSearchParams({ sizes: '1' });
  if (sourceFolder) p.set('sourceFolder', sourceFolder);
  if (projectId) p.set('projectId', projectId);
  if (sharepoint?.driveId && sharepoint?.itemId) { p.set('driveId', sharepoint.driveId); p.set('itemId', sharepoint.itemId); }
  const res = await apiFetch(`/api/pdf-pagecount/${encodeURIComponent(filename)}?${p.toString()}`);
  if (!res.ok) throw new Error(`page sizes ${res.status}`);
  const d = await res.json();
  if (!Array.isArray(d.pages) || !d.pages.length) throw new Error('no page sizes');
  return { pageCount: d.pageCount, pages: d.pages };
};

// Merge a PARTIAL OCR delta (only the files that changed) into the stored blob,
// without re-sending the whole project. Keeps saves tiny during multi-file runs.
// Returns { version } on success, null on failure (caller retries next tick).
export const saveOcrDelta = async (projectId, data, opts = {}) => {
  try {
    const response = await apiFetch(`/api/ocr/${encodeURIComponent(projectId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      // authoritative: files whose array is the whole truth (pages removed on
      // purpose). Any other file is merged PAGE BY PAGE on the server, so a
      // write can never drop pages it does not carry.
      body: JSON.stringify({ data, removed: opts.removed, authoritative: opts.authoritative }),
    });
    if (!response.ok) throw new Error(`OCR delta save failed (${response.status})`);
    return response.json();
  } catch (err) {
    console.warn('[OCR] Delta save failed:', err.message);
    return null;
  }
};

// Admin: push the current file-display config to every project member.
// Stored project-side with a timestamp; clients adopt it once per new push.
export const pushFileDisplayConfig = async (projectId, config) => {
  const response = await apiFetch(`/api/projects/${encodeURIComponent(projectId)}/file-display-config`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ config }),
  });
  if (!response.ok) {
    let msg = 'Failed to push file display config';
    try { const b = await response.json(); if (b?.error) msg = b.error; } catch {}
    throw new Error(msg);
  }
  return response.json();
};

// Delete regions file for a project
export const deleteRegionsFromBackend = async (projectId) => {
  // Invalidate cache
  invalidateCache(cache.regions, projectId);

  const response = await apiFetch(`/api/regions/${encodeURIComponent(projectId)}`, {
    method: 'DELETE'
  });

  if (!response.ok) {
    throw new Error('Failed to delete regions');
  }

  return response.json();
};

// =============================================================================
// Cache Management (for manual control if needed)
// =============================================================================

export const clearAllCaches = () => {
  // Revoke all blob URLs before clearing
  cache.pdfUrls.forEach(url => URL.revokeObjectURL(url));
  
  cache.objects.clear();
  cache.regions.clear();
  cache.models.clear();
  cache.pdfUrls.clear();
  cache.pendingRequests.clear();
};

export const clearProjectCache = (projectId) => {
  invalidateCache(cache.objects, projectId);
  invalidateCache(cache.regions, projectId);
  invalidateCache(cache.models, projectId);
  invalidateCache(cache.models, 'global');
};

// =============================================================================
// TWIN DATA API
// =============================================================================

export async function saveTwinData(projectId, filename, page, data) {
  const encodedFilename = encodeURIComponent(filename);
  const response = await apiFetch(`/api/twin/${projectId}/${encodedFilename}/${page}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ data }),
  });
  if (!response.ok) throw new Error('Failed to save twin data');
  return response.json();
}

export async function getTwinData(projectId, filename, page) {
  const encodedFilename = encodeURIComponent(filename);
  const response = await apiFetch(`/api/twin/${projectId}/${encodedFilename}/${page}`);
  if (!response.ok) throw new Error('Failed to load twin data');
  const result = await response.json();
  return result.data;
}

export async function getTwinEntries(projectId) {
  const response = await apiFetch(`/api/twin/${projectId}`);
  if (!response.ok) return [];
  const result = await response.json();
  return result.entries || [];
}

// ── Cross-file twin graph ──────────────────────────────────────────────
export async function getTwinGraph(projectId) {
  const response = await apiFetch(`/api/twin-graph/${encodeURIComponent(projectId)}`);
  if (!response.ok) return { version: 1, projectId, crossFileEdges: [] };
  const result = await response.json();
  return result.data;
}

export async function saveTwinGraph(projectId, data) {
  const response = await apiFetch(`/api/twin-graph/${encodeURIComponent(projectId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ data }),
  });
  if (!response.ok) throw new Error('Failed to save twin graph');
  return response.json();
}

export async function getAllTwinData(projectId) {
  const response = await apiFetch(`/api/twin-graph/${encodeURIComponent(projectId)}/all-twins`);
  if (!response.ok) return {};
  const result = await response.json();
  return result.twins || {};
}

// ── Recently deleted sub-regions (backend dbRoutes, REGION_TRASH_DAYS) ──
// Only a project owner gets entries back; anyone who may delete regions adds.
export const getRegionTrash = async (projectId) => {
  const response = await apiFetch(`/api/regions/${encodeURIComponent(projectId)}/trash`);
  if (!response.ok) throw new Error('Failed to read recently deleted sub-regions');
  return response.json();
};
export const addRegionTrash = async (projectId, entry) => {
  const response = await apiFetch(`/api/regions/${encodeURIComponent(projectId)}/trash`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ entry }),
  });
  if (!response.ok) throw new Error('Failed to keep the deleted sub-region');
  return response.json();
};
export const removeRegionTrash = async (projectId, entryId) => {
  const response = await apiFetch(`/api/regions/${encodeURIComponent(projectId)}/trash/${encodeURIComponent(entryId)}`, { method: 'DELETE' });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to update recently deleted sub-regions');
  }
  return response.json();
};

// ── Your drafts of unsaved markups (backend markupDraftRoutes, app/workfiles.md) ──
// One per person per document, private, never changes the document.
const draftUrl = (projectId, fileKey) => `/api/projects/${encodeURIComponent(projectId)}/markup-drafts${fileKey != null ? `/${encodeURIComponent(fileKey)}` : ''}`;
export const listMarkupDrafts = async (projectId) => {
  const r = await apiFetch(draftUrl(projectId), { quiet: true });
  if (!r.ok) throw new Error('Failed to read your drafts');
  return r.json();
};
export const getMarkupDraft = async (projectId, fileKey) => {
  const r = await apiFetch(draftUrl(projectId, fileKey), { quiet: true });
  if (!r.ok) throw new Error('Failed to read the draft');
  return r.json();
};
export const putMarkupDraft = async (projectId, fileKey, body, opts = undefined) => {
  const r = await apiFetch(draftUrl(projectId, fileKey), {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), quiet: true, ...(opts || {}),
  });
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    const e = new Error(d.error || 'Failed to keep the draft'); e.status = r.status; throw e;
  }
  return r.json();
};
export const deleteMarkupDraft = async (projectId, fileKey) => {
  const r = await apiFetch(draftUrl(projectId, fileKey), { method: 'DELETE', quiet: true });
  if (!r.ok) throw new Error('Failed to remove the draft');
  return r.json();
};
